'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';

import {
  bulkEditCard,
  bulkEditCardBody,
  bulkEditCardFooter,
} from '../bulk-edit/bulk-edit-styles';
import { BulkEditActionBar, BulkEditToolbar } from '../bulk-edit/components/bulk-edit-action-bar';
import { BulkEditTable } from '../bulk-edit/components/bulk-edit-table';
import {
  BulkEditFooterSkeleton,
  BulkEditToolbarSkeleton,
} from '../bulk-edit/components/bulk-edit-table-skeleton';
import { useBulkEditState } from '../bulk-edit/hooks/use-bulk-edit-state';

interface BulkUpdateProductsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
}

export function BulkUpdateProductsModal({ open, onOpenChange, storeId }: BulkUpdateProductsModalProps) {
  const { t } = useTranslation(['products', 'common']);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const state = useBulkEditState(storeId, { enabled: open });

  const {
    isDirty,
    dirtyCount,
    isSaving,
    isLoading,
    discardChanges,
    saveChanges,
    pagination,
    setPagination,
    total,
    pageCount,
    products,
    selectedKeys,
  } = state;

  useEffect(() => {
    if (!open) setShowDiscardDialog(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty, open]);

  const closeModal = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    closeModal();
  }, [closeModal, isDirty]);

  const handleConfirmDiscard = useCallback(() => {
    discardChanges();
    setShowDiscardDialog(false);
    closeModal();
  }, [closeModal, discardChanges]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      requestClose();
    },
    [onOpenChange, requestClose],
  );

  const table = useReactTable({
    data: products,
    columns: [],
    pageCount,
    rowCount: total,
    state: { pagination },
    getRowId: (row) => row.id,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const showSkeleton = isLoading && products.length === 0;
  const hasSelection = selectedKeys.size > 0;
  const pageLabel = useMemo(() => {
    if (total === 0) return t('products:noProductsFound');
    const page = pagination.pageIndex + 1;
    return t('products:bulkEditor.modalMeta', { total, page, pageCount: Math.max(pageCount, 1) });
  }, [pageCount, pagination.pageIndex, t, total]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="crm-bulk-edit-modal">
          <DialogHeader className="crm-bulk-edit-modal__header">
            <div className="crm-bulk-edit-modal__header-main">
              <div className="crm-bulk-edit-modal__header-copy">
                <DialogTitle className="crm-bulk-edit-modal__title">{t('products:bulkUpdate')}</DialogTitle>
                <div className="crm-bulk-edit-modal__meta">
                  <DialogDescription className="crm-bulk-edit-modal__subtitle">
                    {pageLabel}
                  </DialogDescription>
                  {isDirty ? (
                    <span className="crm-bulk-edit-modal__unsaved">
                      {t('products:bulkEditor.unsavedChanges', { count: dirtyCount })}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="crm-bulk-edit-modal__header-actions">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={requestClose}
                  disabled={isSaving}
                >
                  {t('common:cancel')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void saveChanges()}
                  disabled={!isDirty || isSaving}
                >
                  {isSaving ? t('products:bulkEditor.saving') : t('products:bulkEditor.save')}
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="crm-bulk-edit-modal__body">
            <div className={bulkEditCard} data-zone="products-bulk-edit-modal">
              {showSkeleton ? <BulkEditToolbarSkeleton /> : <BulkEditToolbar state={state} />}

              <div className={bulkEditCardBody}>
                <BulkEditTable state={state} />
              </div>

              <div className={bulkEditCardFooter}>
                {showSkeleton ? (
                  <BulkEditFooterSkeleton />
                ) : (
                  <DataTablePagination table={table} pageSizeOptions={[25, 50, 100]} />
                )}
              </div>
            </div>
          </div>

          {hasSelection ? <BulkEditActionBar state={state} /> : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('products:bulkEditor.discardDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('products:bulkEditor.discardDialog.description', { count: dirtyCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDiscardDialog(false)}>
              {t('common:cancel')}
            </Button>
            <Button type="button" variant="destructive" onClick={handleConfirmDiscard}>
              {t('products:bulkEditor.discardDialog.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
